/**
 * HeroForm: edit form for the `hero` block.
 *
 * data: {
 *   heading, subheading?, backgroundImageUrl?,
 *   ctaText?, ctaLink?,
 *   secondaryCtaText?, secondaryCtaLink?,
 *   audienceItems?: [{ label, description }],
 *   showGraphPreview?: boolean
 * }
 *
 * Plain text inputs for every field. Handles partial/empty `data` (a freshly
 * added hero block starts with empty strings). Each change is reported to the
 * parent via `onChange(nextData)`.
 *
 * Round-3 review finding 4: surfaces inline warnings when the background
 * image URL or the CTA link fails the renderer's allowlist, so the author
 * sees actionable feedback rather than a generic "Failed to save the
 * draft." after the server's validateBlockUrls returns 400. Warnings are
 * advisory: the save button is NOT disabled, because the server-side
 * validator is the security boundary and the user may be mid-paste.
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge, Form, Row, Col, Button } from 'react-bootstrap';

import useCapability from '@/shared/hooks/useCapability';

import { isAllowedLinkUrl, isAllowedImageUrl } from '../../utils/urlValidator';

let _audienceKeyCounter = 0;
const newAudienceKey = () => String(++_audienceKeyCounter);

export default function HeroForm({ data, onChange }) {
    const d = data || {};
    const networkGraphEnabled = useCapability('networkGraph');

    // Finding 1: coerce showGraphPreview to false when the capability is off
    // so a stranded `true` doesn't silently reactivate on flag re-enable.
    // Ref holds the latest d/onChange so the effect dep list stays [networkGraphEnabled]
    // without triggering on every parent re-render.
    const latestRef = useRef(null);
    latestRef.current = { d, onChange };
    useEffect(() => {
        const { d: cur, onChange: cb } = latestRef.current;
        if (!networkGraphEnabled && cur.showGraphPreview) {
            cb({ ...cur, showGraphPreview: false });
        }
    }, [networkGraphEnabled]);

    const update = (field, value) => {
        onChange({ ...d, [field]: value });
    };

    const bgInvalid =
        Boolean(d.backgroundImageUrl) &&
        !isAllowedImageUrl(d.backgroundImageUrl);
    const ctaInvalid = Boolean(d.ctaLink) && !isAllowedLinkUrl(d.ctaLink);
    const secondaryCtaInvalid =
        Boolean(d.secondaryCtaLink) && !isAllowedLinkUrl(d.secondaryCtaLink);

    const audienceItems = d.audienceItems || [];

    // Finding 2: stable React keys so mid-list removal doesn't mis-reconcile
    // controlled inputs. Keys are component-local and never persisted.
    const [audienceKeys, setAudienceKeys] = useState(() =>
        audienceItems.map(() => newAudienceKey())
    );

    const updateAudienceItem = (index, field, value) => {
        const next = audienceItems.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        );
        update('audienceItems', next);
    };

    const addAudienceItem = () => {
        update('audienceItems', [
            ...audienceItems,
            { label: '', description: '' },
        ]);
        setAudienceKeys((keys) => [...keys, newAudienceKey()]);
    };

    const removeAudienceItem = (index) => {
        update(
            'audienceItems',
            audienceItems.filter((_, i) => i !== index)
        );
        setAudienceKeys((keys) => keys.filter((_, i) => i !== index));
    };

    return (
        <>
            <Form.Group className="mb-3" controlId="hero-heading">
                <Form.Label>Heading</Form.Label>
                <Form.Control
                    type="text"
                    value={d.heading || ''}
                    onChange={(e) => update('heading', e.target.value)}
                    placeholder="Main banner heading"
                />
                <Form.Text muted>
                    A hero with no heading is not rendered on the page.
                </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3" controlId="hero-subheading">
                <Form.Label>Subheading</Form.Label>
                <Form.Control
                    type="text"
                    value={d.subheading || ''}
                    onChange={(e) => update('subheading', e.target.value)}
                    placeholder="Optional supporting line"
                />
            </Form.Group>
            <Form.Group className="mb-3" controlId="hero-bg">
                <Form.Label>Background Image URL</Form.Label>
                <Form.Control
                    type="url"
                    value={d.backgroundImageUrl || ''}
                    onChange={(e) =>
                        update('backgroundImageUrl', e.target.value)
                    }
                    placeholder="https://…"
                    isInvalid={bgInvalid}
                />
                {bgInvalid && (
                    <Form.Control.Feedback type="invalid">
                        Background image URL must be http(s) or a same-site path
                        (e.g. /uploads/x.png). data:, javascript:, and
                        protocol-relative URLs are rejected on save.
                    </Form.Control.Feedback>
                )}
            </Form.Group>
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3" controlId="hero-cta-text">
                        <Form.Label>Primary CTA Button Text</Form.Label>
                        <Form.Control
                            type="text"
                            value={d.ctaText || ''}
                            onChange={(e) => update('ctaText', e.target.value)}
                            placeholder="Get started"
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3" controlId="hero-cta-link">
                        <Form.Label>Primary CTA Button Link</Form.Label>
                        <Form.Control
                            type="text"
                            value={d.ctaLink || ''}
                            onChange={(e) => update('ctaLink', e.target.value)}
                            placeholder="/directory"
                            isInvalid={ctaInvalid}
                        />
                        {ctaInvalid && (
                            <Form.Control.Feedback type="invalid">
                                Link must be http(s), mailto:, or a same-site
                                path (e.g. /directory). javascript: and
                                protocol-relative URLs are rejected on save.
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group
                        className="mb-3"
                        controlId="hero-secondary-cta-text"
                    >
                        <Form.Label>Secondary CTA Button Text</Form.Label>
                        <Form.Control
                            type="text"
                            value={d.secondaryCtaText || ''}
                            onChange={(e) =>
                                update('secondaryCtaText', e.target.value)
                            }
                            placeholder="Learn more"
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group
                        className="mb-3"
                        controlId="hero-secondary-cta-link"
                    >
                        <Form.Label>Secondary CTA Button Link</Form.Label>
                        <Form.Control
                            type="text"
                            value={d.secondaryCtaLink || ''}
                            onChange={(e) =>
                                update('secondaryCtaLink', e.target.value)
                            }
                            placeholder="/about"
                            isInvalid={secondaryCtaInvalid}
                        />
                        {secondaryCtaInvalid && (
                            <Form.Control.Feedback type="invalid">
                                Link must be http(s), mailto:, or a same-site
                                path (e.g. /about). javascript: and
                                protocol-relative URLs are rejected on save.
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                </Col>
            </Row>
            <Form.Text muted className="d-block mb-3">
                A CTA button only appears when both its text and link are set.
            </Form.Text>

            <Form.Group className="mb-1">
                <Form.Label className="d-block">Audience Bullets</Form.Label>
                {audienceItems.map((item, i) => (
                    <Row
                        key={audienceKeys[i] ?? i}
                        className="mb-2 align-items-center"
                    >
                        <Col md={4}>
                            <Form.Control
                                type="text"
                                value={item.label || ''}
                                onChange={(e) =>
                                    updateAudienceItem(
                                        i,
                                        'label',
                                        e.target.value
                                    )
                                }
                                placeholder="Audience label"
                                aria-label={`Audience item ${i + 1} label`}
                            />
                        </Col>
                        <Col md={7}>
                            <Form.Control
                                type="text"
                                value={item.description || ''}
                                onChange={(e) =>
                                    updateAudienceItem(
                                        i,
                                        'description',
                                        e.target.value
                                    )
                                }
                                placeholder="Brief description"
                                aria-label={`Audience item ${i + 1} description`}
                            />
                        </Col>
                        <Col md={1} className="text-end">
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeAudienceItem(i)}
                                aria-label={`Remove audience item ${i + 1}`}
                            >
                                &times;
                            </Button>
                        </Col>
                    </Row>
                ))}
                {audienceItems.length < 10 ? (
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={addAudienceItem}
                        className="mt-1"
                    >
                        + Add audience bullet
                    </Button>
                ) : (
                    <Form.Text muted className="d-block mt-1">
                        Maximum of 10 audience bullets reached.
                    </Form.Text>
                )}
            </Form.Group>
            <Form.Text muted className="d-block mb-3">
                Optional list shown below the subheading. Each item has a bold
                label and an optional description.
            </Form.Text>

            <Form.Check
                type="checkbox"
                id="hero-show-graph"
                checked={Boolean(d.showGraphPreview)}
                disabled={!networkGraphEnabled}
                onChange={(e) => update('showGraphPreview', e.target.checked)}
                className="mb-1"
                label={
                    <span className={!networkGraphEnabled ? 'text-muted' : ''}>
                        Show live network graph preview
                        {!networkGraphEnabled && (
                            <Badge bg="secondary" className="ms-2 fw-normal">
                                Feature disabled
                            </Badge>
                        )}
                    </span>
                }
            />
            <Form.Text muted>
                Adds a circular graph preview in a right column on desktop.
            </Form.Text>
        </>
    );
}

HeroForm.propTypes = {
    data: PropTypes.object,
    onChange: PropTypes.func.isRequired,
};

HeroForm.defaultProps = {
    data: {},
};
